import { useState, useEffect, useRef } from 'react';
import { Alert, Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  CustomButton,
} from '../../../common';
import EmailComposeView from './EmailComposeView';
import { buildEmailsPayload } from '../utils/couponsMailsUtils';

const BulkSendConfirmationDialog = ({
  open,
  selectedItems,
  onClose,
  onConfirm,
  plans,
  isDebt,
  cuotaPura,
}) => {
  const [currentEmailIndex, setCurrentEmailIndex] = useState(0);
  const [confirmedEmails, setConfirmedEmails] = useState([]);
  const [notes, setNotes] = useState({});
  const [resetNoteFlag, setResetNoteFlag] = useState(false);
  const recipientCarouselRef = useRef(null);
  const isRecipientCarouselPausedRef = useRef(false);
  const isRecipientCarouselDraggingRef = useRef(false);
  const recipientCarouselDragStartXRef = useRef(0);
  const recipientCarouselDragStartScrollLeftRef = useRef(0);
  const recipientCarouselDirectionRef = useRef(1);

  useEffect(() => {
    if (!open) {
      setCurrentEmailIndex(0);
      setConfirmedEmails([]);
      setNotes({});
    }
  }, [open]);

  const handleNoteChange = (index, newNote) => {
    setNotes((prevNotes) => ({
      ...prevNotes,
      [index]: newNote,
    }));
  };

  const handleNext = () => {
    const currentEmail = {
      ...selectedItems[currentEmailIndex],
      note: notes[currentEmailIndex] || '',
    };

    setConfirmedEmails((prevConfirmed) => [...prevConfirmed, currentEmail]);
    setCurrentEmailIndex((prevIndex) => prevIndex + 1);
    setResetNoteFlag((prev) => !prev);
  };

  const handleSkip = () => {
    setCurrentEmailIndex((prevIndex) => prevIndex + 1);
    setResetNoteFlag((prev) => !prev);
  };

  const handleSkipReview = () => {
    const remainingEmails = selectedItems.slice(currentEmailIndex).map((item, index) => ({
      ...item,
      note: notes[currentEmailIndex + index] || '',
    }));

    setConfirmedEmails((prevConfirmed) => [...prevConfirmed, ...remainingEmails]);
    setCurrentEmailIndex(selectedItems.length);
    setResetNoteFlag((prev) => !prev);
  };

  const isVerificationComplete = currentEmailIndex >= selectedItems.length;
  const progressValue = selectedItems.length
    ? Math.min((currentEmailIndex / selectedItems.length) * 100, 100)
    : 0;
  const pendingCount = Math.max(selectedItems.length - currentEmailIndex, 0);
  const recipientCarouselItems = confirmedEmails;

  const getRecipientName = (item) => {
    const firstName = item.first_name || item.subscriber?.first_name || '';
    const lastName = item.last_name || item.subscriber?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || item.email || item.subscriber?.email || 'Sin nombre';
  };

  useEffect(() => {
    if (!open || !isVerificationComplete || confirmedEmails.length <= 1) return undefined;

    recipientCarouselDirectionRef.current = 1;

    const intervalId = window.setInterval(() => {
      const carousel = recipientCarouselRef.current;
      if (
        !carousel ||
        isRecipientCarouselPausedRef.current ||
        isRecipientCarouselDraggingRef.current
      ) {
        return;
      }

      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
      if (maxScrollLeft <= 0) {
        return;
      }

      if (carousel.scrollLeft >= maxScrollLeft - 1) {
        recipientCarouselDirectionRef.current = -1;
      } else if (carousel.scrollLeft <= 1) {
        recipientCarouselDirectionRef.current = 1;
      }

      carousel.scrollLeft += recipientCarouselDirectionRef.current;
    }, 120);

    return () => window.clearInterval(intervalId);
  }, [confirmedEmails.length, isVerificationComplete, open]);

  const handleRecipientCarouselPointerDown = (event) => {
    const carousel = recipientCarouselRef.current;
    if (!carousel) return;

    isRecipientCarouselDraggingRef.current = true;
    isRecipientCarouselPausedRef.current = true;
    recipientCarouselDragStartXRef.current = event.clientX;
    recipientCarouselDragStartScrollLeftRef.current = carousel.scrollLeft;
    carousel.setPointerCapture?.(event.pointerId);
  };

  const handleRecipientCarouselPointerMove = (event) => {
    const carousel = recipientCarouselRef.current;
    if (!carousel || !isRecipientCarouselDraggingRef.current) return;

    event.preventDefault();
    carousel.scrollLeft =
      recipientCarouselDragStartScrollLeftRef.current -
      (event.clientX - recipientCarouselDragStartXRef.current);
  };

  const handleRecipientCarouselPointerUp = (event) => {
    const carousel = recipientCarouselRef.current;
    isRecipientCarouselDraggingRef.current = false;
    isRecipientCarouselPausedRef.current = false;
    carousel?.releasePointerCapture?.(event.pointerId);
  };

  const handleSendAll = () => {
    if (confirmedEmails.length === 0) {
      return;
    }

    if (isDebt) {
      onConfirm({ mode: 'debt', data: confirmedEmails });
      onClose();
      return;
    }

    onConfirm(buildEmailsPayload(confirmedEmails, plans));
    onClose();
  };

  const renderContent = () => {
    if (!selectedItems.length) {
      return (
        <Alert severity="warning" sx={{ my: 2 }}>
          No hay destinatarios seleccionados para confirmar.
        </Alert>
      );
    }

    if (isVerificationComplete) {
      return (
        <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', py: 4 }}>
          <DoneAllIcon color="primary" sx={{ fontSize: 44 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Revisión lista
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Se enviarán {confirmedEmails.length} de {selectedItems.length} destinatario(s).
            </Typography>
          </Box>
          {confirmedEmails.length > 0 && (
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 800, color: 'text.primary' }}
              >
                Destinatarios
              </Typography>
              <Box
                ref={recipientCarouselRef}
                onPointerDown={handleRecipientCarouselPointerDown}
                onPointerMove={handleRecipientCarouselPointerMove}
                onPointerUp={handleRecipientCarouselPointerUp}
                onPointerCancel={handleRecipientCarouselPointerUp}
                onDragStart={(event) => event.preventDefault()}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  py: 1,
                  cursor: 'grab',
                  userSelect: 'none',
                  touchAction: 'pan-x',
                  overscrollBehaviorX: 'contain',
                  scrollbarWidth: 'none',
                  WebkitMaskImage:
                    'linear-gradient(90deg, transparent 0%, #000 4%, #000 96%, transparent 100%)',
                  maskImage:
                    'linear-gradient(90deg, transparent 0%, #000 4%, #000 96%, transparent 100%)',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridTemplateRows: 'repeat(5, 28px)',
                    gridAutoColumns: 'max-content',
                    alignItems: 'center',
                    width: 'max-content',
                    maxWidth: 'none',
                    columnGap: 0.75,
                    rowGap: 0.6,
                    px: 3,
                  }}
                >
                  {recipientCarouselItems.map((item, index) => (
                    <Chip
                      key={`${index}-${item.subscriber_uuid || item.email || 'recipient'}-${
                        item.contract?.uuid || item.contract_number || ''
                      }`}
                      label={getRecipientName(item)}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{
                        flexShrink: 0,
                        maxWidth: 240,
                        bgcolor: 'transparent',
                        pointerEvents: 'none',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Stack>
      );
    }

    return (
      <EmailComposeView
        open={open}
        emailData={selectedItems[currentEmailIndex]}
        plans={plans}
        onNoteChange={(newNote) => handleNoteChange(currentEmailIndex, newNote)}
        resetNoteFlag={resetNoteFlag}
        isDebt={isDebt}
        cuotaPura={cuotaPura}
      />
    );
  };

  const renderActions = () => {
    if (!selectedItems.length) {
      return (
        <CustomButton onClick={onClose} variant="base">
          Cerrar
        </CustomButton>
      );
    }

    if (isVerificationComplete) {
      return (
        <>
          <CustomButton onClick={onClose} variant="danger">
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={handleSendAll}
            variant="base"
            disabled={confirmedEmails.length === 0}
          >
            {isDebt ? 'Enviar deuda' : 'Enviar todo'}
          </CustomButton>
        </>
      );
    }

    return (
      <>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <CustomButton onClick={handleSkip} variant="warning" buttonIcon={SkipNextIcon}>
            Omitir este
          </CustomButton>
          <CustomButton onClick={handleSkipReview} variant="danger" buttonIcon={DoneAllIcon}>
            Saltar revisión
          </CustomButton>
        </Stack>

        <CustomButton onClick={handleNext} variant="base">
          Siguiente
        </CustomButton>
      </>
    );
  };

  return (
    <DialogContainer open={open} onClose={onClose} size="large" minHeight="auto">
      <DialogTitle>
        {isDebt ? 'Confirmar envío de deuda' : 'Confirmar envío de emails'}
      </DialogTitle>

      <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedItems.length
              ? `Destinatario ${Math.min(currentEmailIndex + 1, selectedItems.length)} de ${
                  selectedItems.length
                }`
              : 'Sin destinatarios'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {pendingCount} pendiente(s)
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: 8,
            borderRadius: 1,
            bgcolor: 'rgba(94, 137, 79, 0.12)',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
            },
          }}
        />
      </Box>

      <DialogBody sx={{ pt: 1 }}>{renderContent()}</DialogBody>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: 1.5,
          p: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {renderActions()}
      </Box>
    </DialogContainer>
  );
};

export default BulkSendConfirmationDialog;
